import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface SessionDataPoint {
  date: string; // ISO date string
  count: number; // Number of sessions on this date
}

export interface SessionAnalytics {
  data: SessionDataPoint[];
  total_sessions: number;
  period: 'day' | 'week' | 'month';
}

export function useSessionAnalytics(period: 'day' | 'week' | 'month' = 'day') {
  return useQuery<SessionAnalytics>({
    queryKey: ['admin', 'session-analytics', period],
    staleTime: 60_000, // 1 minute
    queryFn: async () => {
      const now = new Date();
      let startDate: Date;
      let groupBy: string;

      switch (period) {
        case 'day':
          // Last 30 days, grouped by day
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 30);
          groupBy = 'day';
          break;
        case 'week':
          // Last 12 weeks, grouped by week
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 84); // 12 weeks
          groupBy = 'week';
          break;
        case 'month':
          // Last 12 months, grouped by month
          startDate = new Date(now);
          startDate.setMonth(startDate.getMonth() - 12);
          groupBy = 'month';
          break;
      }

      // Query user_sessions table
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: sessions, error } = await (supabase.from as any)('user_sessions')
        .select('started_at')
        .gte('started_at', startDate.toISOString())
        .lte('started_at', now.toISOString())
        .order('started_at', { ascending: true });

      if (error) {
        return {
          data: [],
          total_sessions: 0,
          period,
        };
      }

      // Group sessions by period
      const grouped: Record<string, number> = {};
      const totalSessions = sessions?.length || 0;

      (sessions as unknown as Array<{ started_at: string }>)?.forEach((session: { started_at: string }) => {
        const date = new Date(session.started_at);
        let key: string;

        switch (groupBy) {
          case 'day':
            key = date.toISOString().split('T')[0]; // YYYY-MM-DD
            break;
          case 'week': {
            // Get week start (Monday)
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay() + 1);
            key = weekStart.toISOString().split('T')[0];
            break;
          }
          case 'month':
            key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // YYYY-MM
            break;
          default:
            key = date.toISOString().split('T')[0];
        }

        grouped[key] = (grouped[key] || 0) + 1;
      });

      // Convert to array and fill in missing dates
      const data: SessionDataPoint[] = [];
      const current = new Date(startDate);
      const end = new Date(now);

      while (current <= end) {
        let key: string;
        let dateLabel: string;

        switch (groupBy) {
          case 'day':
            key = current.toISOString().split('T')[0];
            dateLabel = current.toISOString().split('T')[0];
            current.setDate(current.getDate() + 1);
            break;
          case 'week': {
            const weekStart = new Date(current);
            weekStart.setDate(current.getDate() - current.getDay() + 1);
            key = weekStart.toISOString().split('T')[0];
            dateLabel = weekStart.toISOString().split('T')[0];
            current.setDate(current.getDate() + 7);
            break;
          }
          case 'month':
            key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
            dateLabel = key;
            current.setMonth(current.getMonth() + 1);
            break;
          default:
            key = current.toISOString().split('T')[0];
            dateLabel = key;
            current.setDate(current.getDate() + 1);
        }

        data.push({
          date: dateLabel,
          count: grouped[key] || 0,
        });
      }

      return {
        data,
        total_sessions: totalSessions,
        period,
      };
    },
  });
}

